import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Alert,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Typography,
} from "@mui/material";
import Check from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FlexColumnBox from "../../components/common/FlexBox";
import WeekDatePicker from "../../components/common/WeekDatePicker";
import HotelSelector from "../../components/common/HotelSelector";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import WeekTableReal, {
  DailyData,
  WeekData,
} from "../../components/common/weekTableReal";
import {
  buildOccupationCsvTemplate,
  buildOccupationCsvExport,
} from "../../utils/occupationCsv";
import { useHotels } from "../../hooks/useHotels";
import { useAuth } from "../../context/authContext";
import { addDaysToDate, getLastFriday, resetDateToMidnight } from "../../utils/dateUtils";
import { StatService } from "../../api/statService";
import { Hotel } from "../../types/hotel.d";
import {
  GroupDailyStatsDTO,
  GroupDailyStatsUpsertByDateDTO,
  CategoryDailyStatsDTO,
  CategoryDailyStatsUpsertByDateDTO,
  RealUpdateDto,
} from "../../types/stat.d";
import { ExcelHotelData, importDataFromExcel, parseExcelDate } from "../../utils/importDataFromExcel";

const resetDateToFriday = (date: Date): Date => {
  const base = resetDateToMidnight(date);
  const day = base.getDay();

  let offset;
  if (day === 5) {
    offset = 0;
  } else if (day === 6) {
    offset = -1;
  } else if (day === 0) {
    offset = -2;
  } else {
    offset = -(day + 2);
  }

  base.setDate(base.getDate() + offset);
  return base;
};

const Occupation = () => {
  const today = new Date();
  
  const initialStartDate = getLastFriday(today);

  const excelImportStartCell = 'B6';
  const excelImportEndCell = 'AB12';

  const dateColumn = 'B';
  const totalAvailableNightsCol = 'C';

  const individualOccupiedNightsCol = 'G';
  const individualVisitorsCol = 'I';

  const tourTravelOccupiedNightsCol = 'J';
  const tourTravelVisitorsCol = 'L';

  const groupOccupiedNightsCol = 'M';
  const groupVisitorsCol = 'O';

  const groupUnder50Col = 'R';
  const groupOver50Col = 'S';

  const group0_99NightsCol = 'T';
  const group100_199NightsCol = 'U';
  const groupOver200NightsCol = 'V';

  const groupCorporatifCol = 'X';
  const groupAssociatifCol = 'Y';
  const groupSportCol = 'Z';
  const groupLoisirCol = 'AA';

  const [localStartDate, setLocalStartDate] = useState<Date>(initialStartDate);
  
  const [startDate, setStartDate] = useState<Date>(initialStartDate);
  const endDate = useMemo(() => addDaysToDate(startDate, 6), [startDate]);
  
  const [isLoading, setIsLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const hotels = useHotels();
  const { user } = useAuth();

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const applyDate = useCallback((date: Date) => {
    setStartDate(date);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const debouncedSetDate = useCallback((date: Date) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setStartDate(date);
    }, 3000);
  }, []);

  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [weekData, setWeekData] = useState<WeekData>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStatus, setModalStatus] = useState<
    "success" | "error" | "confirm" | null
  >(null);
  const [modalMessage, setModalMessage] = useState<string | undefined>();

  const [groupStatsByDay, setGroupStatsByDay] = useState<
    Record<number, GroupDailyStatsDTO>
  >({});
  const CAT_IT = 1;
  const CAT_TT = 2;
  const [catStatsByDay, setCatStatsByDay] = useState<
    Record<number, Record<number, { nightsOccupied: number; visitors: number }>>
  >({});
  const [weekIT, setWeekIT] = useState<WeekData>({});
  const [weekTT, setWeekTT] = useState<WeekData>({});
  const [weekGroup, setWeekGroup] = useState<WeekData>({});

  const [excelImportError, setExcelImportError] = useState(false);
  const [excelImportSuccess, setExcelImportSuccess] = useState(false);

  const skipNextDataUpdate = useRef(false);

  const weekRecap: WeekData = useMemo(() => {
    const recap: WeekData = {};

    for (let i = 0; i < 7; i++) {
      const avail =
        weekData[i]?.nuiteesDisponibles ??
        weekGroup[i]?.nuiteesDisponibles ??
        selectedHotel?.maxUnitsCapacity ??
        0;
      const nights =
        (weekIT[i]?.nuiteesOccupees ?? 0) +
        (weekTT[i]?.nuiteesOccupees ?? 0) +
        (weekGroup[i]?.nuiteesOccupees ?? 0);
      const visitors =
        (weekIT[i]?.nombreDeVisiteurs ?? 0) +
        (weekTT[i]?.nombreDeVisiteurs ?? 0) +
        (weekGroup[i]?.nombreDeVisiteurs ?? 0);
      const pct = avail > 0 ? parseFloat(((nights / avail) * 100).toFixed(2)) : undefined;

      recap[i] = {
        nuiteesDisponibles: avail,
        nuiteesOccupees: nights,
        nombreDeVisiteurs: visitors,
        pourcentageVendues: pct,
        tauxOccupation: pct !== undefined ? `${pct.toFixed(2)}%` : "-",
      };
    }
    return recap;
  }, [selectedHotel, weekIT, weekTT, weekGroup, weekData]);

  const formatWeekData = useCallback(
    (data: any[], maxUnitsCapacity: number): WeekData => {
      const week: WeekData = {};

      for (let i = 0; i < 7; i++) {
        week[i] = {
          nuiteesDisponibles: maxUnitsCapacity,
          nuiteesOccupees: 0,
          nombreDeVisiteurs: 0,
          tauxOccupation: "-",
          pourcentageVendues: undefined,
        };
      }

      data.forEach((item) => {
        const dayIndex = Math.round(
          (new Date(item.date).getTime() - startDate.getTime()) /
            (1000 * 60 * 60 * 24)
        );

        if (dayIndex >= 0 && dayIndex < 7) {
          const taux =
            item.available_Units > 0
              ? parseFloat(((item.sold_Units / item.available_Units) * 100).toFixed(2))
              : undefined;

          const base: DailyData = {
            nuiteesDisponibles: item.available_Units ?? maxUnitsCapacity,
            nuiteesOccupees: item.sold_Units ?? 0,
            nombreDeVisiteurs: item.number_Of_Visitors ?? 0,
            tauxOccupation: taux !== undefined ? `${taux.toFixed(2)}%` : "-",
            pourcentageVendues: taux,
          };
          week[dayIndex] = base;
        }
      });

      return week;
    },
    [startDate]
  );

  useEffect(() => {
    if (user?.role !== "Admin" && user?.hotel) {
      setSelectedHotel(user.hotel);
    }
  }, [user]);

  useEffect(() => {
    if (!selectedHotel) return;

    if(skipNextDataUpdate.current) {
      skipNextDataUpdate.current = false;
      return;
    }

    const fetchOccupationData = async () => {
      // Réinitialiser tous les états à zéro avant le fetch
      setWeekData({});
      setWeekIT({});
      setWeekTT({});
      setWeekGroup({});
      setGroupStatsByDay({});
      setCatStatsByDay({});
      
      try {
        const data = await StatService.getOccupation(
          selectedHotel.id,
          startDate,
          endDate
        );
        const formatted = formatWeekData(data, selectedHotel.maxUnitsCapacity);

        const groupStats = await StatService.getGroupDailyStats(
          selectedHotel.id,
          startDate,
          endDate
        );
        const mapGroup: Record<number, GroupDailyStatsDTO> = {};
        const isoToIndex2: Record<string, number> = {};
        for (let i = 0; i < 7; i++) {
          const iso = addDaysToDate(startDate, i).toISOString().split("T")[0];
          isoToIndex2[iso] = i;
        }
        groupStats.forEach((g) => {
          if (!g.date) return;
          const dayIndex = isoToIndex2[g.date];
          if (dayIndex === undefined) return;
          mapGroup[dayIndex] = {
            date: g.date,
            hotelId: selectedHotel.id,
            groupNightsOccupied: g.groupNightsOccupied ?? 0,
            groupVisitors: g.groupVisitors ?? 0,
            groupsCampBaseUsed: g.groupsCampBaseUsed ?? 0,
            groupNightsMultiHotels: g.groupNightsMultiHotels ?? 0,
            groupCountUnder50: g.groupCountUnder50 ?? 0,
            groupCountOver50: g.groupCountOver50 ?? 0,
            groupCountNights0_99: g.groupCountNights0_99 ?? 0,
            groupCountNights100_199: g.groupCountNights100_199 ?? 0,
            groupCountNights200Plus: g.groupCountNights200Plus ?? 0,
            groupCountCorporatif: g.groupCountCorporatif ?? 0,
            groupCountAssociatif: g.groupCountAssociatif ?? 0,
            groupCountSport: g.groupCountSport ?? 0,
            groupCountLoisir: g.groupCountLoisir ?? 0,
          } as GroupDailyStatsDTO;
        });

        setGroupStatsByDay(mapGroup);

        const wg: WeekData = {};
        for (let i = 0; i < 7; i++) {
          const avail =
            (formatted[i]?.nuiteesDisponibles ??
              selectedHotel.maxUnitsCapacity) ||
            0;
          const gs = mapGroup[i];
          wg[i] = {
            nuiteesDisponibles: avail,
            nuiteesOccupees: gs?.groupNightsOccupied ?? 0,
            nombreDeVisiteurs: gs?.groupVisitors ?? 0,
            tauxOccupation:
              avail > 0
                ? `${(((gs?.groupNightsOccupied ?? 0) / avail) * 100).toFixed(2)}%`
                : "-",
            pourcentageVendues:
              avail > 0
                ? parseFloat((((gs?.groupNightsOccupied ?? 0) / avail) * 100).toFixed(2))
                : undefined,
          };
        }

        const categoryStats = await StatService.getCategoryDailyStats(
          selectedHotel.id,
          startDate,
          endDate
        );
        const catMap: Record<number, Record<number, { nightsOccupied: number; visitors: number }>> = {};
        for (let i = 0; i < 7; i++) {
          if (!catMap[i]) catMap[i] = {};
        }
        categoryStats.forEach((c: CategoryDailyStatsDTO) => {
          if (!c.date) return;
          const idx = isoToIndex2[c.date];
          if (idx === undefined) return;
          if (!catMap[idx]) catMap[idx] = {};
          catMap[idx][c.category] = {
            nightsOccupied: c.nightsOccupied ?? 0,
            visitors: c.visitors ?? 0,
          };
        });

        setCatStatsByDay(catMap);
        setWeekGroup(wg);
        setWeekData(formatted);
      } catch (error) {
        console.error("Erreur lors de la récupération des données :", error);
      }
    };

    fetchOccupationData();
  }, [selectedHotel, startDate, endDate, formatWeekData]);

  useEffect(() => {
    if (!selectedHotel) return;
    const wIT: WeekData = {};
    const wTT: WeekData = {};
    for (let i = 0; i < 7; i++) {
      const avail =
        weekData[i]?.nuiteesDisponibles ?? selectedHotel.maxUnitsCapacity;
      const nightsIT = catStatsByDay[i]?.[CAT_IT]?.nightsOccupied ?? 0;
      const nightsTT = catStatsByDay[i]?.[CAT_TT]?.nightsOccupied ?? 0;
      const visitorsIT = catStatsByDay[i]?.[CAT_IT]?.visitors ?? 0;
      const visitorsTT = catStatsByDay[i]?.[CAT_TT]?.visitors ?? 0;
      const pctIT =
        avail > 0 ? parseFloat(((nightsIT / avail) * 100).toFixed(2)) : undefined;
      const pctTT =
        avail > 0 ? parseFloat(((nightsTT / avail) * 100).toFixed(2)) : undefined;
      wIT[i] = {
        nuiteesDisponibles: avail,
        nuiteesOccupees: nightsIT,
        nombreDeVisiteurs: visitorsIT,
        pourcentageVendues: pctIT,
        tauxOccupation: pctIT !== undefined ? `${pctIT.toFixed(2)}%` : "-",
      };
      wTT[i] = {
        nuiteesDisponibles: avail,
        nuiteesOccupees: nightsTT,
        nombreDeVisiteurs: visitorsTT,
        pourcentageVendues: pctTT,
        tauxOccupation: pctTT !== undefined ? `${pctTT.toFixed(2)}%` : "-",
      };
    }
    setWeekIT(wIT);
    setWeekTT(wTT);
  }, [selectedHotel, weekData, catStatsByDay]);

  const handleSend = async () => {
    if (!selectedHotel) {
      alert("Veuillez sélectionner un hôtel !");
      return;
    }

    setIsLoading(true);

    // Préparer les données : un enregistrement total + enregistrements par type de visiteur
    const transformedData: RealUpdateDto[] = [];
    
    for (let i = 0; i < 7; i++) {
      const date = addDaysToDate(startDate, i);
      const iso = date.toISOString().split("T")[0];
      const avail =
        weekGroup[i]?.nuiteesDisponibles ??
        weekData[i]?.nuiteesDisponibles ??
        selectedHotel.maxUnitsCapacity ??
        0;
      const sold =
        (weekIT[i]?.nuiteesOccupees ?? 0) +
        (weekTT[i]?.nuiteesOccupees ?? 0) +
        (weekGroup[i]?.nuiteesOccupees ?? 0);
      const totalVisitors =
        (weekIT[i]?.nombreDeVisiteurs ?? 0) +
        (weekTT[i]?.nombreDeVisiteurs ?? 0) +
        (weekGroup[i]?.nombreDeVisiteurs ?? 0);
      
      // Enregistrement principal avec totaux (RealResult)
      transformedData.push({
        hotelId: selectedHotel.id,
        date: iso,
        sold_Units: sold,
        available_Units: avail,
        number_Of_Visitors: totalVisitors,
        // Sans visitorTypeId : enregistre seulement RealResult
      });
      
      //TODO: Vérifier si cette partie du code devrait fonctionner.
      // En ce moment, le backend met automatiquement le visitorTypeId à 1 s'il est null (comme dans le data en haut),
      // donc les données sont envoyées en double.

      // Visiteurs IT + T&T (Destination = visitorTypeId 1) pour VisitorCounter
      // IT et T&T sont tous deux de type "Destination"
      // const destinationVisitors = (weekIT[i]?.nombreDeVisiteurs ?? 0) + (weekTT[i]?.nombreDeVisiteurs ?? 0);
      // if (destinationVisitors > 0) {
      //   transformedData.push({
      //     hotelId: selectedHotel.id,
      //     date: iso,
      //     sold_Units: 0,
      //     available_Units: avail,
      //     number_Of_Visitors: destinationVisitors,
      //     visitorTypeId: 1, // IT + T&T = Destination
      //   });
      // }
    }

    try {
      await StatService.sendOccupation(transformedData);

      const groupItems: GroupDailyStatsUpsertByDateDTO[] = [];
      for (let i = 0; i < 7; i++) {
        const d = addDaysToDate(startDate, i);
        const iso = d.toISOString().split("T")[0];
        const g = groupStatsByDay[i];
        groupItems.push({
          hotelId: selectedHotel.id,
          date: iso,
          groupNightsOccupied: g?.groupNightsOccupied ?? 0,
          groupVisitors: g?.groupVisitors ?? 0,
          groupsCampBaseUsed: g?.groupsCampBaseUsed ?? 0,
          groupNightsMultiHotels: g?.groupNightsMultiHotels ?? 0,
          groupCountUnder50: g?.groupCountUnder50 ?? 0,
          groupCountOver50: g?.groupCountOver50 ?? 0,
          groupCountNights0_99: g?.groupCountNights0_99 ?? 0,
          groupCountNights100_199: g?.groupCountNights100_199 ?? 0,
          groupCountNights200Plus: g?.groupCountNights200Plus ?? 0,
          groupCountCorporatif: g?.groupCountCorporatif ?? 0,
          groupCountAssociatif: g?.groupCountAssociatif ?? 0,
          groupCountSport: g?.groupCountSport ?? 0,
          groupCountLoisir: g?.groupCountLoisir ?? 0,
        });
      }
      await StatService.upsertGroupDailyStatsByDate(groupItems);

      const catItems: CategoryDailyStatsUpsertByDateDTO[] = [];
      for (let i = 0; i < 7; i++) {
        const d = addDaysToDate(startDate, i);
        const iso = d.toISOString().split("T")[0];
        catItems.push({
          hotelId: selectedHotel.id,
          date: iso,
          category: CAT_IT,
          nightsOccupied: weekIT[i]?.nuiteesOccupees ?? 0,
          visitors: weekIT[i]?.nombreDeVisiteurs ?? 0,
        });
        catItems.push({
          hotelId: selectedHotel.id,
          date: iso,
          category: CAT_TT,
          nightsOccupied: weekTT[i]?.nuiteesOccupees ?? 0,
          visitors: weekTT[i]?.nombreDeVisiteurs ?? 0,
        });
      }
      await StatService.upsertCategoryDailyStatsByDate(catItems);
      setModalStatus("success");
      setModalMessage(
        "Les données d'occupation ont été envoyées avec succès !"
      );
    } catch (error) {
      console.error("Erreur lors de l'envoi :", error);
      setModalStatus("error");
      setModalMessage(
        "Une erreur est survenue lors de l'envoi. Veuillez réessayer."
      );
    } finally {
      setIsLoading(false);
      setModalOpen(true);
    }
  };

  const handleConfirm = () => {
    setModalStatus("confirm");
    setModalMessage(
      "Êtes-vous sûr de vouloir envoyer ces données d'occupation ?"
    );
    setModalOpen(true);
  };

  const handleHotelChange = useCallback((hotel: Hotel | null) => {
    setSelectedHotel(hotel);
  }, []);

  const generateCsvTemplate = () => buildOccupationCsvTemplate(startDate);

  const downloadTemplate = () => {
    const csv = generateCsvTemplate();
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);
    a.download = `AVT_GABARIT_${startStr}_${endStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    const hotelName = selectedHotel?.name ?? "SansHotel";
    const hotelToken = hotelName
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^A-Za-z0-9_-]/g, "")
      .toUpperCase();
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = endDate.toISOString().slice(0, 10);
    const fileName = `AVT_${hotelToken}_${startStr}_${endStr}.csv`;
    const csv = buildOccupationCsvExport(
      startDate,
      weekData,
      weekIT,
      weekTT,
      weekGroup,
      groupStatsByDay,
      selectedHotel?.maxUnitsCapacity ?? 0
    );
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  const onFileSelected = async (file: File | null) => {
     const allowedFileExtension = 'xlsx';
    
      if (!file || file.name.split('.').pop() !== allowedFileExtension) return;
    
      const buffer = await file.arrayBuffer();
    
      const data = await importDataFromExcel(buffer, excelImportStartCell, excelImportEndCell);
    
      if(typeof data !== "boolean")
        excelImport(data);
  };

  const excelImport = async (data: ExcelHotelData) => {
    skipNextDataUpdate.current = true;
    
    const startDate = Object.values(data[dateColumn])[0];
    
    const date = parseExcelDate(startDate);

    if(typeof(date) === "boolean") {
      setExcelImportError(true);
      return
    }
    
    setStartDate(date);
    setLocalStartDate(date);

    const totalAvailableNights = Object.values(data[totalAvailableNightsCol]);
    const totalWeekData : WeekData = {};

    const individualOccupiedNights = Object.values(data[individualOccupiedNightsCol]);
    const individualVisitors = Object.values(data[individualVisitorsCol]);

    const tourTravelOccupiedNights = Object.values(data[tourTravelOccupiedNightsCol]);
    const tourTravelVisitors = Object.values(data[tourTravelVisitorsCol]);

    if(!individualOccupiedNights.every(v => !isNaN(parseInt(v))) || !individualVisitors.every(v => !isNaN(parseInt(v))) 
    || !tourTravelOccupiedNights.every(v => !isNaN(parseInt(v))) || !tourTravelVisitors.every(v => !isNaN(parseInt(v)))
    || !totalAvailableNights.every(v => !isNaN(parseInt(v)))) {
      setExcelImportError(true);
      return;
    }
    
    const travellerData: Record<number, Record<number, { nightsOccupied: number; visitors: number }>> = {};

    const groupOccupiedNights = Object.values(data[groupOccupiedNightsCol]);
    const groupVisitors = Object.values(data[groupVisitorsCol]);
    const groupUnder50 = Object.values(data[groupUnder50Col]);
    const groupOver50 = Object.values(data[groupOver50Col]);
    const group0_99Nights = Object.values(data[group0_99NightsCol]);
    const group100_199Nights = Object.values(data[group100_199NightsCol]);
    const groupOver200Nights = Object.values(data[groupOver200NightsCol]);
    const groupCorporatif = Object.values(data[groupCorporatifCol]);
    const groupAssociatif = Object.values(data[groupAssociatifCol]);
    const groupSport = Object.values(data[groupSportCol]);
    const groupLoisir = Object.values(data[groupLoisirCol]);

    const groupData : Record<number, GroupDailyStatsDTO> = {};
    const weekGroupData : WeekData = {};

    for(let day = 0; day < 7; day++) {
      const availableNights = parseInt(totalAvailableNights[day]);

      totalWeekData[day] = {};
      totalWeekData[day].nuiteesDisponibles = availableNights;

      travellerData[day] = {};
      travellerData[day][CAT_IT] = {
        nightsOccupied: parseInt(individualOccupiedNights[day]),
        visitors: parseInt(individualVisitors[day])
      };
      travellerData[day][CAT_TT] = {
        nightsOccupied: parseInt(tourTravelOccupiedNights[day]),
        visitors: parseInt(tourTravelVisitors[day])
      };

      groupData[day] = {
        date: startDate,
        groupNightsOccupied: parseInt(groupOccupiedNights[day]) || 0,
        groupVisitors: parseInt(groupVisitors[day]) || 0,
        groupsCampBaseUsed : 0,
        groupNightsMultiHotels : 0,
        groupCountUnder50 : parseInt(groupUnder50[day]) || 0,
        groupCountOver50 : parseInt(groupOver50[day]) || 0,
        groupCountNights0_99 : parseInt(group0_99Nights[day]) || 0,
        groupCountNights100_199 : parseInt(group100_199Nights[day]) || 0,
        groupCountNights200Plus : parseInt(groupOver200Nights[day]) || 0,
        groupCountCorporatif : parseInt(groupCorporatif[day]) || 0,
        groupCountAssociatif : parseInt(groupAssociatif[day]) || 0,
        groupCountSport : parseInt(groupSport[day]) || 0,
        groupCountLoisir : parseInt(groupLoisir[day]) || 0
      };

      weekGroupData[day] = {};
      const groupNights = parseInt(groupOccupiedNights[day]);
      weekGroupData[day].nuiteesOccupees = groupNights;
      weekGroupData[day].nombreDeVisiteurs = parseInt(groupVisitors[day]);
      weekGroupData[day].tauxOccupation = parseFloat(((groupNights / availableNights) * 100).toFixed(2)) + '%'
    }

    setWeekData(totalWeekData);
    setCatStatsByDay(travellerData);
    setGroupStatsByDay(groupData);
    setWeekGroup(weekGroupData);
    setExcelImportSuccess(true);
    setImportOpen(false);
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) onFileSelected(file);
  };

  const handleDataChangeIT = (day: number, updated: DailyData) => {
    setWeekIT((prev) => ({
      ...prev,
      [day]: { ...prev[day], ...updated },
    }));
    if (updated.nuiteesOccupees !== undefined || updated.nombreDeVisiteurs !== undefined) {
      setCatStatsByDay((prev) => ({
        ...prev,
        [day]: {
          ...(prev[day] || {}),
          [CAT_IT]: {
            nightsOccupied: updated.nuiteesOccupees ?? prev[day]?.[CAT_IT]?.nightsOccupied ?? 0,
            visitors: updated.nombreDeVisiteurs ?? prev[day]?.[CAT_IT]?.visitors ?? 0,
          },
        },
      }));
    }
  };
  const handleDataChangeTT = (day: number, updated: DailyData) => {
    setWeekTT((prev) => ({
      ...prev,
      [day]: { ...prev[day], ...updated },
    }));
    if (updated.nuiteesOccupees !== undefined || updated.nombreDeVisiteurs !== undefined) {
      setCatStatsByDay((prev) => ({
        ...prev,
        [day]: {
          ...(prev[day] || {}),
          [CAT_TT]: {
            nightsOccupied: updated.nuiteesOccupees ?? prev[day]?.[CAT_TT]?.nightsOccupied ?? 0,
            visitors: updated.nombreDeVisiteurs ?? prev[day]?.[CAT_TT]?.visitors ?? 0,
          },
        },
      }));
    }
  };
  const handleDataChangeGroup = (day: number, updated: DailyData) => {
    setWeekGroup((prev) => ({
      ...prev,
      [day]: { ...prev[day], ...updated },
    }));
    setGroupStatsByDay((prev) => {
      const date = addDaysToDate(startDate, day).toISOString().split("T")[0];
      const existing =
        prev[day] ||
        ({ date, hotelId: selectedHotel?.id } as GroupDailyStatsDTO);
      return {
        ...prev,
        [day]: {
          ...existing,
          date,
          hotelId: selectedHotel?.id,
          groupNightsOccupied:
            updated.nuiteesOccupees !== undefined
              ? (updated.nuiteesOccupees as number)
              : existing.groupNightsOccupied ?? 0,
          groupVisitors:
            updated.nombreDeVisiteurs !== undefined
              ? (updated.nombreDeVisiteurs as number)
              : existing.groupVisitors ?? 0,
          groupNightsMultiHotels: existing.groupNightsMultiHotels ?? 0,
          groupsCampBaseUsed: existing.groupsCampBaseUsed ?? 0,
          groupCountUnder50: existing.groupCountUnder50 ?? 0,
          groupCountOver50: existing.groupCountOver50 ?? 0,
          groupCountNights0_99: existing.groupCountNights0_99 ?? 0,
          groupCountNights100_199: existing.groupCountNights100_199 ?? 0,
          groupCountNights200Plus: existing.groupCountNights200Plus ?? 0,
          groupCountCorporatif: existing.groupCountCorporatif ?? 0,
          groupCountAssociatif: existing.groupCountAssociatif ?? 0,
          groupCountSport: existing.groupCountSport ?? 0,
          groupCountLoisir: existing.groupCountLoisir ?? 0,
        },
      };
    });
  };

  return (
    <FlexColumnBox margin="auto" mt={5} mb={5} alignItems="center" gap={5}>
      <Box
        width="90%"
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="h5" sx={{ textAlign: "center", width: "100%" }}>
          Occupation réelle
        </Typography>
        <Box sx={{ position: "absolute", right: 0, display: "flex", gap: 1 }}>
          <Tooltip title="Importer Excel">
            <span>
              <IconButton
                color="primary"
                onClick={() => setImportOpen(true)}
                disabled={!selectedHotel}
                size="large"
              >
                <FileUploadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Exporter CSV">
            <span>
              <IconButton
                color="primary"
                onClick={exportCsv}
                disabled={!selectedHotel}
                size="large"
              >
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        display="flex"
        gap={2}
        mb={4}
        flexWrap="wrap"
        justifyContent="center"
      >
        {user?.role === "Admin" && (
          <HotelSelector
            hotels={hotels}
            selectedHotel={selectedHotel}
            onChange={handleHotelChange}
          />
        )}
        <WeekDatePicker
          startDate={localStartDate}
          setStartDate={(date) => {
            const aligned = resetDateToFriday(date);
            setLocalStartDate(aligned);
            debouncedSetDate(aligned);
          }}
          onApply={(date) => {
            const aligned = resetDateToFriday(date);
            setLocalStartDate(aligned);
            applyDate(aligned);
          }}
        />
      </Box>

      <WeekTableReal
        week="Total tous segments"
        startDate={startDate}
        rows={[
          {
            label: "Nuitées\ndisponibles",
            editable: true,
            field: "nuiteesDisponibles",
          },
          {
            label: "Nuitées occupées\ntotales",
            editable: false,
            field: "nuiteesOccupees",
          },
          {
            label: "Nombre de visiteurs\ntotaux",
            editable: false,
            field: "nombreDeVisiteurs",
          },
          {
            label: "Taux\nd'occupation",
            editable: false,
            field: "tauxOccupation",
          },
        ]}
        weekData={weekRecap}
        onDataChange={(day, updated) => {
          if (updated.nuiteesDisponibles !== undefined) {
            const newAvail = updated.nuiteesDisponibles;

            setWeekData((prev) => ({
              ...prev,
              [day]: {
                ...(prev[day] || {}),
                nuiteesDisponibles: newAvail,
              },
            }));

            setWeekIT((prev) => ({
              ...prev,
              [day]: {
                ...(prev[day] || {}),
                nuiteesDisponibles: newAvail,
              },
            }));

            setWeekTT((prev) => ({
              ...prev,
              [day]: {
                ...(prev[day] || {}),
                nuiteesDisponibles: newAvail,
              },
            }));
            
            setWeekGroup((prev) => ({
              ...prev,
              [day]: {
                ...(prev[day] || {}),
                nuiteesDisponibles: newAvail,
              },
            }));
          }
        }}
      />

      <WeekTableReal
        week="Individual Travelers (IT)"
        startDate={startDate}
        rows={[
          {
            label: "Nuitées\noccupées",
            editable: true,
            field: "nuiteesOccupees",
          },
          {
            label: "Nombre de\nvisiteurs",
            editable: true,
            field: "nombreDeVisiteurs",
          },
          {
            label: "Taux\nd'occupation",
            editable: false,
            field: "tauxOccupation",
          },
        ]}
        weekData={weekIT}
        onDataChange={handleDataChangeIT}
      />

      <WeekTableReal
        week="Tour & Travel (T&T)"
        startDate={startDate}
        rows={[
          {
            label: "Nuitées\noccupées",
            editable: true,
            field: "nuiteesOccupees",
          },
          {
            label: "Nombre de\nvisiteurs",
            editable: true,
            field: "nombreDeVisiteurs",
          },
          {
            label: "Taux\nd'occupation",
            editable: false,
            field: "tauxOccupation",
          },
        ]}
        weekData={weekTT}
        onDataChange={handleDataChangeTT}
      />

      <WeekTableReal
        week="Groupes"
        startDate={startDate}
        rows={[
          {
            label: "Nuitées Groupes\noccupées",
            editable: true,
            field: "nuiteesOccupees",
          },
          {
            label: "Nombre de\nvisiteurs",
            editable: true,
            field: "nombreDeVisiteurs",
          },
          {
            label: "Taux\nd'occupation",
            editable: false,
            field: "tauxOccupation",
          },
        ]}
        weekData={weekGroup}
        onDataChange={handleDataChangeGroup}
        renderVisitorCollapse={({ dates }) => (
          <Accordion
            disableGutters
            defaultExpanded
            sx={{ boxShadow: "none", mt: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                Groupes — Détail par sous-types
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table
                size="small"
                sx={{
                  width: "100%",
                  backgroundColor: "#fff",
                  tableLayout: "fixed",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    {dates.map(({ label }, i) => (
                      <TableCell
                        key={i}
                        align="center"
                        sx={{ fontWeight: 600 }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { key: "groupCountUnder50", label: "< 50\npersonnes" },
                    { key: "groupCountOver50", label: "> 50\npersonnes" },
                    { key: "groupCountNights0_99", label: "0 - 99\nnuitées" },
                    {
                      key: "groupCountNights100_199",
                      label: "100 - 199\nnuitées",
                    },
                    { key: "groupCountNights200Plus", label: "200 nuitées\n+" },
                    { key: "groupCountCorporatif", label: '"CORPORATIF"' },
                    { key: "groupCountAssociatif", label: '"ASSOCIATIF"' },
                    { key: "groupCountSport", label: '"SPORT"' },
                    { key: "groupCountLoisir", label: '"LOISIR"' },
                  ].map((f, idx) => (
                    <TableRow
                      key={f.key}
                      sx={{
                        backgroundColor: idx % 2 === 0 ? "#fafafa" : "#fff",
                      }}
                    >
                      <TableCell sx={{ whiteSpace: "pre-line", wordWrap: "break-word" }}>
                        {f.label}
                      </TableCell>
                      {Array.from({ length: 7 }, (_, dayIndex) => {
                        const v = groupStatsByDay[dayIndex]?.[
                          f.key as keyof GroupDailyStatsDTO
                        ] as number | undefined;
                        return (
                          <TableCell key={dayIndex} align="center">
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0 }}
                              value={v ?? 0}
                              onChange={(e) => {
                                const value = Number(e.target.value);
                                if (value < 0) {
                                  return;
                                }
                                setGroupStatsByDay((prev) => ({
                                  ...prev,
                                  [dayIndex]: {
                                    ...(prev[dayIndex] || {}),
                                    [f.key]: value,
                                  } as GroupDailyStatsDTO,
                                }));
                              }}
                              sx={{ 
                                width: 80,
                                "& .MuiInputBase-input": {
                                  padding: "6px 4px",
                                  fontSize: "0.875rem",
                                },
                              }}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        )}
      />

      <Button
        variant="contained"
        endIcon={<Check />}
        onClick={handleConfirm}
        disabled={!selectedHotel}
      >
        Confirmer
      </Button>

      <ConfirmationModal
        isOpen={modalOpen}
        status={modalStatus}
        message={modalMessage}
        onClose={() => setModalOpen(false)}
        onConfirm={modalStatus === "confirm" ? handleSend : undefined}
        isLoading={isLoading}
      />

      <Dialog open={excelImportError} onClose={() => setExcelImportError(false)}>
              <DialogTitle>Votre fichier ne semble pas respecter le gabarit</DialogTitle>
              <DialogContent>
                <Typography align="center">Veuillez valider le fichier que vous avez sélectionné</Typography>
              </DialogContent>
            </Dialog>
      
      <Dialog open={excelImportSuccess} onClose={() => setExcelImportSuccess(false)} >
        <DialogTitle align="center">Données importées avec succès</DialogTitle>
        <DialogContent>
          <Typography align="center">Veuillez valider les données et appuyer sur le bouton confirmer au bas de la page
            pour les sauvegarder.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Importer des données (Excel)</DialogTitle>
        <DialogContent>
          <Alert
            icon={<InfoOutlinedIcon fontSize="small" />}
            severity="info"
            sx={{ mb: 2 }}
          >
            Utilisez le gabarit de la semaine pour éviter les erreurs d’import.
            Les en-têtes et l’ordre des colonnes doivent rester identiques.
          </Alert>
              <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                <Stack spacing={1}>
                  <Typography
                    variant="subtitle1"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <UploadFileIcon color="primary" /> Importer les données
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Glissez-déposez votre fichier .xlsx ici, ou utilisez le
                    bouton pour le sélectionner.
                  </Typography>
                  <Box
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      mt: 1,
                      p: 3,
                      border: "2px dashed",
                      borderColor: dragActive ? "primary.main" : "divider",
                      borderRadius: 2,
                      textAlign: "center",
                      color: "text.secondary",
                      backgroundColor: dragActive
                        ? "action.hover"
                        : "transparent",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      document.getElementById("excel-file-input")?.click()
                    }
                  >
                    Déposez le fichier ici
                  </Box>
                  <input
                    id="excel-file-input"
                    type="file"
                    accept=".xlsx"
                    style={{ display: "none" }}
                    onChange={(e) =>
                      onFileSelected(e.target.files?.[0] || null)
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    Format accepté: .xlsx
                  </Typography>
                </Stack>
              </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </FlexColumnBox>
  );
};

export default Occupation;
